import { PageHero, LocalBusinessSchema } from '@components/common';
import { CALENDLY_URL } from '@constants';
import { Shield, Target, Users, Clock, MapPin } from 'lucide-react';
import PageFAQ from '@components/sections/PageFAQ';

export default function BelfastPage() {
  return (
    <>
      <LocalBusinessSchema city="Belfast" latitude={54.5973} longitude={-5.9301} />
      <PageHero
        title="Fractional CISO Services in Belfast"
        subtitle="Cybersecurity leadership for Northern Ireland's digital transformation"
        eyebrow="Belfast"
        seoTitle="Fractional CISO Belfast | The CISO Network"
        seoDescription="Expert fractional CISO services for Belfast businesses. Access senior cybersecurity leadership for Northern Ireland's growing tech sector."
      />

      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-6">
                Securing Northern Ireland's Cyber Security Capital
              </h2>
              <div className="space-y-4 text-muted-foreground">
                <p>
                  Belfast has emerged as one of Europe's most significant cybersecurity hubs, with a
                  thriving ecosystem spanning cyber security, fintech, legal technology, and
                  software development. The city's Titanic Quarter and Cathedral Quarter have become
                  magnets for tech investment, with major global firms and ambitious local startups
                  creating a dynamic digital economy.
                </p>
                <p>
                  Northern Irish businesses face distinctive cybersecurity challenges. The region's
                  prominence in cybersecurity means local firms must demonstrate exceptional
                  security credentials. Fintech and legaltech companies serving UK and international
                  clients face complex regulatory requirements. Meanwhile, businesses working with
                  public sector clients must meet stringent government security standards.
                </p>
                <p>
                  Despite Belfast's reputation as a cyber hub, many local businesses struggle to
                  access the C-level security expertise they need. The demand for senior security
                  professionals often outstrips supply, and full-time CISOs command premium
                  salaries. Our fractional CISO service provides Belfast businesses with executive
                  security leadership that understands the unique Northern Irish business landscape.
                </p>
                <p>
                  Belfast has developed into one of the UK's most significant cybersecurity and
                  technology hubs, with major investment in digital infrastructure and a growing
                  startup ecosystem. Northern Ireland's unique regulatory position requires security
                  leaders who understand both UK and cross-border data protection requirements. A
                  fractional CISO with NI experience brings this essential perspective.
                </p>
              </div>
            </div>
            <div className="space-y-6">
              <div className="p-6 rounded-xl bg-background border-l-4 border-teal">
                <div className="flex items-start gap-4">
                  <MapPin className="h-6 w-6 text-teal flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-heading font-semibold text-foreground mb-2">
                      Your Account Manager
                    </h3>
                    <p className="text-muted-foreground text-sm">
                      Your account manager understands Northern Ireland's unique cyber security
                      landscape and business environment.
                    </p>
                  </div>
                </div>
              </div>
              <div className="p-6 rounded-xl bg-background border-l-4 border-teal">
                <div className="flex items-start gap-4">
                  <Clock className="h-6 w-6 text-teal flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-heading font-semibold text-foreground mb-2">
                      NI-Wide Coverage
                    </h3>
                    <p className="text-muted-foreground text-sm">
                      Supporting businesses across Belfast, Derry, and throughout Northern Ireland
                      with local CISO expertise.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-background">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              Why Belfast Businesses Choose Fractional CISOs
            </h2>
            <p className="text-muted-foreground max-w-3xl mx-auto">
              Expert security leadership from the heart of Europe's cyber hub
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: Shield,
                title: 'Cyber Sector Expertise',
                description:
                  "Work with CISOs who understand Northern Ireland's unique cyber security ecosystem.",
              },
              {
                icon: Target,
                title: 'Cost Effective',
                description:
                  'Access senior expertise without competing for scarce full-time CISO talent.',
              },
              {
                icon: Users,
                title: 'Regulatory Knowledge',
                description:
                  'Navigate UK, Irish, and international regulatory requirements with confidence.',
              },
              {
                icon: Clock,
                title: 'Flexible Engagement',
                description:
                  'Scale security leadership as your Belfast business grows across markets.',
              },
            ].map((benefit, i) => (
              <div key={i} className="text-center">
                <div className="h-14 w-14 mx-auto rounded-xl bg-teal flex items-center justify-center mb-5">
                  <benefit.icon className="h-7 w-7 text-accent-foreground" />
                </div>
                <h3 className="text-lg font-heading font-semibold text-foreground mb-2">
                  {benefit.title}
                </h3>
                <p className="text-muted-foreground text-sm">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <PageFAQ
        title="Fractional CISO Services in Belfast - FAQs"
        subtitle="Common questions from Belfast and Northern Ireland businesses"
        items={[
          {
            id: 'belfast-1',
            question: 'How does a fractional CISO work with businesses in Belfast?',
            answer:
              'Our fractional CISOs work with organisations across the UK, including Belfast. They can attend meetings on-site or work remotely, depending on what suits your business. We match you with a CISO who understands the security challenges facing cybersecurity, public sector, and technology companies.',
          },
          {
            id: 'belfast-2',
            question: 'What size of business benefits from a fractional CISO in Belfast?',
            answer:
              'Fractional CISO services are ideal for growing businesses that need strategic security leadership but are not yet ready for a full-time executive hire. This includes Belfast startups, scale-ups, and mid-sized companies that are winning larger contracts or facing regulatory requirements.',
          },
          {
            id: 'belfast-3',
            question: 'What industries do your CISOs cover in Belfast?',
            answer:
              "Our network includes CISOs with experience in cybersecurity, public sector, and technology. We match you with someone who understands your sector's specific regulatory environment and threat landscape, whether you are navigating FCA requirements, NHS Digital standards, or customer security questionnaires.",
          },
          {
            id: 'belfast-4',
            question: 'What is the first thing a fractional CISO does when they start?',
            answer:
              'Every engagement begins with discovery. Your CISO assesses your current security posture, understands your business priorities, and develops a tailored roadmap. This typically delivers actionable insights within the first 30 days, with quick wins identified early.',
          },
          {
            id: 'belfast-5',
            question: 'Can a fractional CISO help with board reporting and governance?',
            answer:
              'Yes. A core part of the fractional CISO role is translating technical security risks into business language for boards and leadership teams. Our CISOs build reporting frameworks, present to boards, and establish governance structures that satisfy regulators and investors.',
          },
        ]}
      />

      <section className="py-24 gradient-navy cta-photo">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-primary-foreground mb-6">
            Ready to Secure Your Belfast Business?
          </h2>
          <p className="text-primary-foreground/60 text-lg max-w-3xl mx-auto mb-10">
            Speak with our team today and discover how a fractional CISO can protect your digital
            ambitions.
          </p>
          <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="btn-hero">
            Book a Call
          </a>
        </div>
      </section>
    </>
  );
}
